import React, { useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ChevronLeft } from "lucide-react";
import { LogoMark } from "./Logo";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";

const PUBLIC_LINKS = [
  { to: "/", label: "Home" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/about", label: "About" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export function PublicHeader() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth() || {};
  const navigate = useNavigate();

  return (
    <header
      data-testid="public-header"
      className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-[#E5E7EB]"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
        <Link to="/" data-testid="header-logo-link" className="flex items-center">
          <LogoMark />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {PUBLIC_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${
                  isActive ? "text-[#0A2540]" : "text-[#666666] hover:text-[#0A2540]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              <Button
                data-testid="header-dashboard-btn"
                variant="ghost"
                onClick={() => navigate(user.role === "admin" ? "/admin" : "/dashboard")}
                className="text-[#0A2540]"
              >
                Dashboard
              </Button>
              <Button
                data-testid="header-logout-btn"
                onClick={async () => {
                  await logout();
                  navigate("/");
                }}
                className="bg-[#0A2540] hover:bg-[#133D6B] text-white"
              >
                Sign out
              </Button>
            </>
          ) : (
            <>
              <Button
                data-testid="header-login-btn"
                variant="ghost"
                onClick={() => navigate("/login")}
                className="text-[#0A2540]"
              >
                Log in
              </Button>
              <Button
                data-testid="header-signup-btn"
                onClick={() => navigate("/signup")}
                className="bg-[#00C4B4] hover:bg-[#009E91] text-white rounded-full px-5"
              >
                Become a Merchant
              </Button>
            </>
          )}
        </div>

        {/* Mobile hamburger — extreme right */}
        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-md border border-[#E5E7EB] text-[#0A2540]"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="md:hidden border-t border-[#E5E7EB] bg-white">
          <div className="px-5 py-3 flex flex-col gap-1">
            {PUBLIC_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => setOpen(false)}
                className="py-3 text-sm font-medium text-[#0A2540]"
              >
                {l.label}
              </Link>
            ))}
            <div className="border-t border-[#E5E7EB] my-2" />
            {user ? (
              <>
                <Link to={user.role === "admin" ? "/admin" : "/dashboard"} onClick={() => setOpen(false)} className="py-3 text-sm font-semibold text-[#0A2540]">
                  Dashboard
                </Link>
                <button
                  onClick={async () => {
                    setOpen(false);
                    await logout();
                    navigate("/");
                  }}
                  className="py-3 text-left text-sm font-semibold text-[#0A2540]"
                >
                  Sign out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="py-3 text-sm font-semibold text-[#0A2540]">
                  Log in
                </Link>
                <Link to="/signup" onClick={() => setOpen(false)} className="py-3 text-sm font-semibold text-[#00C4B4]">
                  Become a Merchant →
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

export function BackButton({ to, label = "Back" }) {
  const navigate = useNavigate();
  return (
    <button
      data-testid="back-button"
      onClick={() => (to ? navigate(to) : navigate(-1))}
      className="flex items-center text-sm font-medium text-[#666666] hover:text-[#0A2540] transition-colors gap-2 mb-6"
    >
      <ChevronLeft size={16} />
      {label}
    </button>
  );
}

export function Footer() {
  return (
    <footer className="border-t border-[#E5E7EB] bg-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 grid grid-cols-1 md:grid-cols-4 gap-8">
        <div className="md:col-span-2">
          <LogoMark />
          <p className="mt-4 text-sm text-[#666666] max-w-md leading-relaxed">
            FlexiVouch is a multi-utility stored-value digital voucher platform.
            One voucher, multiple services — XLearna Premium, airtime, data,
            electricity and more, all with partial redemption.
          </p>
          <p className="mt-4 text-xs text-[#999999]">A brand owned and operated by Praxio (Pty) Ltd.</p>
        </div>
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#00C4B4]">Praxio (Pty) Ltd</p>
          <p className="mt-3 text-sm text-[#333333]">31 5th Street<br />Commercia<br />Midrand, 1685</p>
          <p className="mt-3 text-sm text-[#333333]">
            Tel: <a href="tel:0794852928" className="text-[#0A2540] font-medium">079 485 2928</a>
            <br />
            <a href="mailto:info@praxio.co.za" className="text-[#0A2540] font-medium">info@praxio.co.za</a>
          </p>
        </div>
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#00C4B4]">Legal</p>
          <ul className="mt-3 space-y-2 text-sm">
            <li><Link to="/terms" className="text-[#0A2540] hover:underline">Terms & Conditions</Link></li>
            <li><Link to="/privacy" className="text-[#0A2540] hover:underline">Privacy Policy</Link></li>
            <li><Link to="/contact" className="text-[#0A2540] hover:underline">Contact us</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-5 flex flex-col sm:flex-row justify-between gap-2 text-xs text-[#999999]">
          <p>© {new Date().getFullYear()} Praxio (Pty) Ltd. All rights reserved.</p>
          <p>Built for South Africa · Powered by FlexiVouch</p>
        </div>
      </div>
    </footer>
  );
}

export function PublicShell({ children }) {
  const loc = useLocation();
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [loc.pathname]);
  return (
    <div className="min-h-screen flex flex-col">
      <PublicHeader />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
